import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/// The top of every page that is not a picture: its title at one size, whatever the page is
/// for - an administrative page does not get a smaller heading than a shelf. Beside the title
/// goes what qualifies it (a count, a kind), at the far end what can be done here, and under it
/// a line that says what the page is for, where it needs one.
export default function PageHeader({
  title,
  aside,
  actions,
  description,
  onBack,
}: {
  title: ReactNode;
  aside?: ReactNode;
  actions?: ReactNode;
  description?: ReactNode;
  /// A page one level down - a plugin's settings - has a way back up in front of its title.
  onBack?: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', minHeight: 44 }}>
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: 'baseline', minWidth: 0, flexWrap: 'wrap' }}>
          {onBack && (
            <IconButton
              aria-label={t('common.back')}
              onClick={onBack}
              sx={{ alignSelf: 'center', marginInlineStart: -1 }}
            >
              <ArrowBackIcon sx={{ transform: theme.direction === 'rtl' ? 'scaleX(-1)' : undefined }} />
            </IconButton>
          )}

          <Typography variant="h2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {title}
          </Typography>

          {aside && (
            <Box sx={{ color: 'text.secondary', typography: 'body1' }}>
              {aside}
            </Box>
          )}
        </Stack>

        {actions && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ marginInlineStart: 'auto', alignItems: 'center', flexWrap: 'wrap' }}
          >
            {actions}
          </Stack>
        )}
      </Stack>

      {description && (
        <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
          {description}
        </Typography>
      )}
    </Stack>
  );
}
